/**
 * Login page — splits screen with brand side + form side.
 * Demo users: admin/admin123, vendedor/ventas123, user/user123.
 */
import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Leaf, LogIn } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/Button'

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('admin123')
  const [loading, setLoading] = useState(false)

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await login(username, password)
      toast.success('Sesión iniciada')
      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch (err) {
      toast.error(err.message || 'Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden md:flex flex-1 bg-expo-dark text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="bg-expo-yellow p-3 rounded-xl">
            <Leaf size={40} className="text-expo-dark" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">EXPOGREENS</h1>
            <p className="text-white/80">Gestión de Exportación de Follajes</p>
          </div>
        </div>
        <div>
          <h2 className="text-5xl font-bold mb-4 leading-tight">
            Del cultivo
            <br />
            al mundo.
          </h2>
          <p className="text-white/80 text-lg max-w-md">
            Gestiona ventas, compras, inventario y devoluciones desde un mismo panel.
          </p>
        </div>
        <p className="text-white/60 text-sm">© {new Date().getFullYear()} Expogreens</p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center bg-expo-light p-8">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md bg-white rounded-2xl shadow-lg p-10 space-y-6"
        >
          <div>
            <h2 className="text-2xl font-bold text-expo-dark">Iniciar sesión</h2>
            <p className="text-gray-500 mt-1">Ingresa tus credenciales para continuar</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-expo-dark mb-1.5">
              Usuario
            </label>
            <input
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-expo-primary focus:ring-2 focus:ring-expo-primary/20 outline-none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-expo-dark mb-1.5">
              Contraseña
            </label>
            <input
              type="password"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-expo-primary focus:ring-2 focus:ring-expo-primary/20 outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            <LogIn size={22} />
            <span>{loading ? 'Ingresando…' : 'Iniciar sesión'}</span>
          </Button>

          <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
            <p className="font-medium text-gray-700 mb-1">Usuarios demo:</p>
            <p>admin / admin123 &middot; vendedor / ventas123 &middot; user / user123</p>
          </div>
        </form>
      </div>
    </div>
  )
}
